import React from 'react';
import {
  LogIn, LayoutDashboard, Bell, Contact, PhoneCall, ListChecks,
  MessageSquare, MessageCircle, LifeBuoy, RefreshCw, UserCog,
} from 'lucide-react';
import ScreenGallery from './ScreenGallery';
import ANDROID_SCREENS from '../data/androidScreens';

/**
 * The Android reference.
 *
 * Written from the app as it actually runs, captured on a real handset —
 * the section this replaces described a bottom tab bar of "Home,
 * Follow-up, Leads, Reports, Settings" that the app does not have. What it
 * has is a navigation drawer, and these are its entries in drawer order.
 *
 * Every module here is agent-facing on purpose: the Android app is what an
 * agent works in all day, while admin configuration stays on the web
 * dashboard. Where a screen is missing from a module it is because the
 * signed-in role could not reach it, not because it does not exist.
 */
export const ANDROID_MODULES = [
  {
    id: 'and-login',
    slug: 'login',
    icon: LogIn,
    accent: 'accent-brand',
    name: 'Signing in',
    summary:
      'Three fields: the organisation code, then the agent’s email or mobile, then the password. The org code is what points the app at your company — the same app serves every organisation, so without it the login has nowhere to go.',
  },
  {
    id: 'and-dashboard',
    slug: 'dashboard',
    icon: LayoutDashboard,
    accent: 'accent-1',
    name: 'Home',
    summary:
      'The agent’s own day, not the organisation’s. A status card at the top (Available, Meeting, and so on) that a supervisor sees live, four MY WORK tiles — new customers, open tasks, follow-ups due today, open opportunities — and a REPORTS block that switches between today, this week and this month.',
  },
  {
    id: 'and-notifications',
    slug: 'notifications',
    icon: Bell,
    accent: 'accent-3',
    name: 'Notifications',
    summary:
      'Assignments and reminders, each with a View action that opens the record it refers to. Campaign assignments arrive here, which is how an agent learns they have been added to a calling list.',
  },
  {
    id: 'and-customers',
    slug: 'customers',
    icon: Contact,
    accent: 'accent-5',
    name: 'Customers',
    summary:
      'Two lists — the ones assigned to this agent, and everything the role is allowed to see. Opening a record gives the contact actions (SMS, call, WhatsApp, email) above four tabs: Activities, Opportunities, Follow-ups and Logs. An opportunity can be created straight from the row.',
  },
  {
    id: 'and-sip-call',
    slug: 'sip-call',
    icon: PhoneCall,
    accent: 'accent-6',
    name: 'Dialer',
    summary:
      'Where an agent spends the day. Pick a campaign, set availability (Available, On break, Wrap-up), and work the queue. Four tabs carry the session: the customer queue, the call log, follow-ups due, and the agent’s own performance. Auto-dial walks the list without the agent choosing each number.',
  },
  {
    id: 'and-tasks',
    slug: 'tasks',
    icon: ListChecks,
    accent: 'accent-4',
    name: 'Tasks',
    summary:
      'Work assigned to this agent, filtered by status along the top — New Lead, Contacted, Follow-up and the rest, each with a live count. A task carries its assignee, customer, campaign, priority and due date, and can be edited in place.',
  },
  {
    id: 'and-messages',
    slug: 'messages',
    icon: MessageSquare,
    accent: 'accent-2',
    name: 'Messages',
    summary:
      'Internal chat, split into direct and group conversations. This is the same messaging as the web dashboard, so a supervisor can answer from a desk while the agent is on the floor, and unread counts follow the user across both.',
  },
  {
    id: 'and-sms',
    slug: 'sms',
    icon: MessageCircle,
    accent: 'accent-7',
    name: 'SMS',
    summary:
      'Text messages on the handset’s own SIM, filtered by all, unread and read. Because it is the device’s SIM rather than a gateway, replies land in the same thread the customer already has with that number.',
  },
  {
    id: 'and-support',
    slug: 'support',
    icon: LifeBuoy,
    accent: 'accent-8',
    name: 'Support',
    summary:
      'Tickets raised by or assigned to this agent, in two views. It is the in-app route to whoever runs your WASLX instance, so an agent does not have to leave the app to report a problem.',
  },
  {
    id: 'and-offline-sync',
    slug: 'offline-sync',
    icon: RefreshCw,
    accent: 'accent-6',
    name: 'Offline & Sync',
    summary:
      'The screen that makes field work possible. It shows online/offline state, when the last sync ran, whether anything is still queued, and a Work offline switch. Call recording status and the microphone permission are reported here too, per device.',
  },
  {
    id: 'and-profile',
    slug: 'profile',
    icon: UserCog,
    accent: 'accent-5',
    name: 'My profile',
    summary:
      'Name, email, phone and role, with an optional password change. The role shown here is what decides which of the modules above the agent can open at all.',
  },
];

const AndroidCard = ({ module }) => {
  const Icon = module.icon;
  const screens = ANDROID_SCREENS[module.slug] || [];

  return (
    <article id={module.id} className={`${module.accent} doc-module scroll-mt-24`}>
      <header className="doc-module__head">
        <span className="icon-tile">
          <Icon className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="doc-module__title">{module.name}</h3>
          <p className="doc-module__who">Android app</p>
        </div>
        {screens.length > 0 && (
          <span className="doc-module__count">
            {screens.length}{' '}
            <span className="doc-module__count-unit">
              {screens.length === 1 ? 'screen' : 'screens'}
            </span>
          </span>
        )}
      </header>

      <div className="doc-module__body doc-module__body--solo">
        <div className="doc-module__copy">
          <p className="leading-relaxed text-content-muted">{module.summary}</p>

          {screens.length > 0 && (
            <>
              <p className="doc-module__label doc-module__label--tight">
                Screens
                <span className="doc-module__hint">click to enlarge</span>
              </p>
              <ScreenGallery screens={screens} title={module.name} variant="phone" />
            </>
          )}
        </div>
      </div>
    </article>
  );
};

const AndroidShowcase = () => (
  <div className="doc-module-stack">
    {ANDROID_MODULES.map(module => (
      <AndroidCard key={module.id} module={module} />
    ))}
  </div>
);

export default AndroidShowcase;
